/**
 * guestMealStorage.js — iter1091
 *
 * Пази последното анонимно "trial" хранене от landing widget-a в sessionStorage
 * и позволява post-register auto-log в diary → безшевен conversion.
 *
 * Стратегия: sessionStorage (не localStorage), защото:
 *  • Trial-анализите се очаква да са ephemeral (не искаме да "висят" за
 *    хора, които са затворили таба).
 *  • GDPR: nothing persists past window close for unauthenticated users.
 *
 * Формат:
 *  {
 *    version: 1,
 *    saved_at: ISO string,
 *    kind: "text" | "photo" | "barcode",
 *    description?: string,        // за text tab
 *    analysis: { food_name, calories, protein_g, carbs_g, fat_g,
 *                fiber_g, sugar_g, saturated_fat_g, sodium_mg,
 *                nis_percent, dil_g, twin_conversation, ... }
 *  }
 */

const KEY = "nutramilo_guest_meal";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — expired записите се игнорират

export function saveGuestMeal({ kind, description, analysis }) {
  if (!analysis || typeof analysis !== "object") return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        version: 1,
        saved_at: new Date().toISOString(),
        kind: kind || "text",
        description: description || null,
        analysis,
      }),
    );
  } catch { /* quota exceeded — silent */ }
}

export function readGuestMeal() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !parsed.analysis) return null;
    if (parsed.saved_at) {
      const age = Date.now() - new Date(parsed.saved_at).getTime();
      if (age > MAX_AGE_MS) {
        sessionStorage.removeItem(KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearGuestMeal() {
  try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
}

/**
 * Log the stored guest meal to the user's diary via `/api/meals/add`.
 * Called from Register.jsx след успешна verifyEmail + refreshUser.
 *
 * @param {string} apiBase — REACT_APP_BACKEND_URL + "/api"
 * @param {string} token — access token (Bearer)
 * @returns {Promise<{logged: boolean, id?: string, error?: string}>}
 */
export async function autoLogGuestMealIfAny(apiBase, token) {
  const stored = readGuestMeal();
  if (!stored) return { logged: false };
  const a = stored.analysis || {};
  const grams = a.portion_g || 100;
  const body = {
    meal_type: guessMealTypeByHour(),
    food_name: a.food_name || (stored.description || "").slice(0, 60) || "Trial meal",
    amount_g: Number(grams),
    calories: Number(a.calories || 0),
    protein: Number(a.protein_g || 0),
    carbs: Number(a.carbs_g || 0),
    fat: Number(a.fat_g || 0),
    fiber: Number(a.fiber_g || 0),
    sugar: a.sugar_g != null ? Number(a.sugar_g) : 0,
    saturated_fat: a.saturated_fat_g != null ? Number(a.saturated_fat_g) : 0,
    sodium: a.sodium_mg != null ? Number(a.sodium_mg) : null,
    source: "guest_trial_widget",
  };
  try {
    const res = await fetch(`${apiBase}/meals/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      return { logged: false, error: `HTTP ${res.status}` };
    }
    const data = await res.json();
    clearGuestMeal();
    return { logged: true, id: data?.id || data?.meal_id };
  } catch (e) {
    return { logged: false, error: String(e) };
  }
}

function guessMealTypeByHour() {
  const h = new Date().getHours();
  if (h < 11) return "breakfast";
  if (h < 15) return "lunch";
  if (h < 18) return "snack";
  return "dinner";
}
